import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import useLenis from "@/lib/useLenis";
import useSEO, { useJsonLd } from "@/lib/useSEO";
import useContent from "@/lib/useContent";
import Nav from "@/sections/Nav";
import Footer from "@/sections/Footer";
import { Toaster } from "@/components/ui/sonner";
import { JOURNEYS } from "@/lib/packages-data";
import { CONTACT, SITE_URL } from "@/lib/site-data";
import {
  INDIA_PATH,
  INDIA_SEO,
  INDIA_WA_MESSAGE,
  INDIA_HERO,
  INDIA_FACTS,
  INDIA_ROUTES,
  INDIA_INCLUDES,
  INDIA_DOCUMENTS,
  INDIA_SDF,
  INDIA_FAQS,
} from "@/lib/india-data";

const EASE = [0.76, 0, 0.24, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1571401835393-8c5f35328320?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const WA_NUMBER = CONTACT.phone1.replace(/[^0-9]/g, "");
const waLink = (msg) => `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: EASE },
};

function WhatsAppCTA({ message = INDIA_WA_MESSAGE, label = "Get Indian rates on WhatsApp", light = false, testid }) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`btn-sharp inline-flex items-center gap-3 px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-medium border ${
        light ? "border-[#F5F2EB] text-[#F5F2EB]" : "border-foreground/80 text-foreground"
      }`}
    >
      {label}
      <span aria-hidden>→</span>
    </a>
  );
}

function SectionLabel({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-6">{children}</div>;
}

function Hero() {
  return (
    <section className="relative min-h-[88vh] overflow-hidden bg-[#0A0B0A] text-[#F5F2EB]">
      <img
        src={HERO_IMG}
        alt="Prayer flags above a valley in Bhutan"
        className="absolute inset-0 w-full h-full object-cover opacity-55"
        draggable="false"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0A] via-[#0A0B0A]/30 to-[#0A0B0A]/50" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 min-h-[88vh] flex flex-col justify-end pb-20 md:pb-28">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#F5F2EB]/70 mb-6 flex items-center gap-3">
            <span className="w-6 h-px bg-[#D1492E]" />
            {INDIA_HERO.overline}
          </div>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.94] tracking-tighter max-w-5xl">
            {INDIA_HERO.title}
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg font-light text-[#F5F2EB]/80 leading-relaxed">{INDIA_HERO.intro}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
            <WhatsAppCTA light testid="india-hero-whatsapp" />
            <a
              href={`tel:${CONTACT.phone1.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-3 px-2 py-4 text-[11px] uppercase tracking-[0.22em] font-medium text-[#F5F2EB]/85 hover:text-[#D1492E] transition-colors"
            >
              <span className="w-8 h-px bg-current" />
              Call {CONTACT.phone1}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Facts() {
  return (
    <section className="border-b border-foreground/15 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-y-8 py-10 md:py-12">
        {INDIA_FACTS.map(([n, l]) => (
          <div key={n} className="pr-6">
            <div className="font-serif text-2xl md:text-3xl tracking-tight">{n}</div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-foreground/55">{l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function JourneyGrid({ journeys }) {
  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div {...reveal} className="max-w-3xl">
          <SectionLabel>Popular with Indian guests</SectionLabel>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.98] tracking-tighter">
            Choose a journey. <em className="italic text-primary">We'll send the rate.</em>
          </h2>
          <p className="mt-6 text-base md:text-lg font-light text-foreground/65 leading-relaxed">
            Every journey is private and can be shortened, extended or combined. Tap “Ask for Indian rate” and we'll reply on WhatsApp with a quote for your dates.
          </p>
        </motion.div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {journeys.map((j) => (
            <motion.article key={j.slug} {...reveal} data-testid={`india-journey-${j.slug}`}>
              <Link to={`/journeys/${j.slug}`} className="block group">
                <div className="aspect-[4/3] overflow-hidden bg-foreground/5">
                  <img
                    src={j.img}
                    alt={`${j.title} — ${j.category || "Bhutan"} tour in Bhutan`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                </div>
                <div className="mt-5 text-[10px] uppercase tracking-[0.24em] text-foreground/55">
                  {[j.duration, j.category].filter(Boolean).join(" · ")}
                </div>
                <h3 className="mt-2 font-serif text-2xl md:text-3xl tracking-tight group-hover:text-primary transition-colors">{j.title}</h3>
                {j.subtitle && <div className="mt-1 text-sm text-foreground/60">{j.subtitle}</div>}
              </Link>
              <div className="mt-5 flex items-center gap-6 text-[11px] uppercase tracking-[0.2em] font-medium">
                <a
                  href={waLink(`Hi Planet Air Adventure, I'm travelling from India. Please share your Indian guest rate for "${j.title}".`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Ask for Indian rate →
                </a>
                <Link to={`/journeys/${j.slug}`} className="text-foreground/60 hover:text-foreground transition-colors">
                  Itinerary
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function GettingThere() {
  return (
    <section className="py-20 md:py-28 bg-[#0A0B0A] text-[#F5F2EB]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div {...reveal}>
          <SectionLabel>Getting there</SectionLabel>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.98] tracking-tighter max-w-3xl">
            Two ways into Bhutan from India
          </h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-2 gap-10 md:gap-16">
          {INDIA_ROUTES.map((r, i) => (
            <motion.div key={r.title} {...reveal} className="border-t border-[#F5F2EB]/20 pt-8">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#D1492E] mb-4">0{i + 1}</div>
              <h3 className="font-serif text-3xl tracking-tight">{r.title}</h3>
              <p className="mt-4 text-base font-light text-[#F5F2EB]/70 leading-relaxed">{r.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ListBox({ label, items }) {
  return (
    <div className="border border-foreground/15 p-7 md:p-9 h-full">
      <SectionLabel>{label}</SectionLabel>
      <ul className="space-y-4">
        {items.map((x) => (
          <li key={x} className="flex gap-4 text-base font-light text-foreground/80 leading-relaxed">
            <span className="mt-2.5 w-1.5 h-1.5 bg-primary rotate-45 shrink-0" />
            {x}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Essentials() {
  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div {...reveal} className="max-w-3xl">
          <SectionLabel>Before you travel</SectionLabel>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[0.98] tracking-tighter">
            Permits, ID and SDF — <em className="italic text-primary">made simple.</em>
          </h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8">
          <ListBox label="What we handle" items={INDIA_INCLUDES} />
          <ListBox label="Documents to carry" items={INDIA_DOCUMENTS} />
          <ListBox label="Sustainable Development Fee" items={INDIA_SDF} />
        </div>
      </div>
    </section>
  );
}

function IndiaFAQ() {
  return (
    <section className="py-20 md:py-28 bg-background border-t border-foreground/15">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-4">
          <SectionLabel>Questions from Indian travellers</SectionLabel>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-[0.98] tracking-tighter">Good to know</h2>
        </div>
        <div className="md:col-span-8 divide-y divide-foreground/15 border-y border-foreground/15">
          {INDIA_FAQS.map((f) => (
            <div key={f.q} className="py-7">
              <h3 className="font-serif text-xl md:text-2xl tracking-tight">{f.q}</h3>
              <p className="mt-3 text-base font-light text-foreground/70 leading-relaxed max-w-2xl">{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-24 md:py-32 bg-[#0A0B0A] text-[#F5F2EB]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 text-center">
        <h2 className="font-serif font-light text-4xl md:text-7xl leading-[0.96] tracking-tighter">
          Special rates for <em className="italic text-[#D1492E]">Indian guests</em>
        </h2>
        <p className="mt-6 mx-auto max-w-xl text-base md:text-lg font-light text-[#F5F2EB]/70 leading-relaxed">
          Send your dates and number of travellers on WhatsApp. A curator in Paro replies within 24 hours.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <WhatsAppCTA light label={`WhatsApp ${CONTACT.phone1}`} testid="india-final-whatsapp" />
          <a href={`mailto:${CONTACT.email}`} className="text-[11px] uppercase tracking-[0.22em] text-[#F5F2EB]/75 hover:text-[#D1492E] transition-colors">
            {CONTACT.email}
          </a>
        </div>
      </div>
    </section>
  );
}

export default function IndiaLanding() {
  useLenis();
  useSEO({ title: INDIA_SEO.title, description: INDIA_SEO.description, path: INDIA_PATH, image: HERO_IMG });
  const journeys = useContent("journeys", JOURNEYS);
  // ids match scripts/prerender.mjs so the pre-built schema is replaced, not duplicated
  useJsonLd("india-faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: INDIA_FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  });
  useJsonLd("breadcrumb", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Bhutan tours from India", item: `${SITE_URL}${INDIA_PATH}` },
    ],
  });

  return (
    <div className="App relative">
      <div className="grain" aria-hidden />
      <Nav />
      <main>
        <Hero />
        <Facts />
        <JourneyGrid journeys={journeys} />
        <GettingThere />
        <Essentials />
        <IndiaFAQ />
        <FinalCTA />
      </main>
      <Footer />
      <Toaster />
    </div>
  );
}
